"use client"

import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { useState, useEffect } from "react"
import { Loader2, CheckCircle, XCircle, GraduationCap, User, Phone, Mail, MapPin, Calendar, BookOpen } from "lucide-react"

export default function CollegeAdmissionPage() {
  const [formData, setFormData] = useState({
    branch_name: "",
    last_name: "",
    first_name: "",
    father_name: "",
    mother_name: "",
    phone_no: "",
    whatsapp_no: "",
    aadhar_number: "",
    email_id: "",
    gender: "",
    dob: "",
    admission_year: "2026",
    stream: "",
    medium: "",
    address: ""
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null
    message: string
  }>({ type: null, message: "" })

  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: "" })

    try {
      console.log("🚀 Starting submission process...")

      const payload = new FormData()

      // Map every field to the FormData object
      Object.entries(formData).forEach(([key, value]) => {
        // Special mapping: PHP expects 'year' for the admission_year column
        if (key === 'admission_year') {
          payload.append('year', value)
        } else {
          payload.append(key, value)
        }
      })

      console.log("📤 Sending data to proxy API...")

      const response = await fetch("/api/college-admission", {
        method: "POST",
        body: payload,
      })

      console.log("📥 Status received:", response.status)
      const result = await response.json()
      console.log("📥 Full API Result:", result)

      // Check for success in multiple ways (status: success, success: true, or status: 1)
      const isSuccessful = result.success === true ||
        result.status === 'success' ||
        result.status === '1' ||
        result.status === 1 ||
        (typeof result === 'string' && result.includes('1'));

      if (isSuccessful && !result.message?.toLowerCase().includes("error")) {
        setSubmitStatus({
          type: "success",
          message: "Application submitted successfully! Our team will contact you soon."
        })

        // Reset the form fields
        setFormData({
          branch_name: "",
          last_name: "",
          first_name: "",
          father_name: "",
          mother_name: "",
          phone_no: "",
          whatsapp_no: "",
          aadhar_number: "",
          email_id: "",
          gender: "",
          dob: "",
          admission_year: "2026",
          stream: "",
          medium: "",
          address: ""
        })
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Log the exact message from server to help debugging
        const errorMsg = result.message || result.raw || "The server rejected the application.";
        throw new Error(errorMsg);
      }

    } catch (error: any) {
      console.error("❌ Submission error details:", error)
      setSubmitStatus({
        type: "error",
        message: error.message || "An unexpected error occurred during submission."
      })
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f8fbff] font-sans selection:bg-blue-100 selection:text-blue-900">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 opacity-95"></div>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className={`transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <GraduationCap className="w-16 h-16 text-blue-300 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 tracking-tight">
              Unlock Your Future
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto font-light">
              Join Lokmanya & Bharti Academy for a transformative educational experience. Academic Excellence for the Session 2026-27.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 -mt-10 pb-20 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[380px,1fr] gap-8">

            {/* Sidebar / Instructions */}
            <div className={`transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <div className="bg-white rounded-2xl shadow-xl shadow-blue-900/5 border border-blue-100 overflow-hidden sticky top-24">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white text-center">
                  <h2 className="text-xl font-bold uppercase tracking-wider">Registration Guide</h2>
                </div>

                <div className="p-8 space-y-8">
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold border border-blue-100">1</div>
                      <p className="text-sm text-gray-600 leading-relaxed font-medium">Complete the form carefully with accurate details to receive your application number.</p>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold border border-blue-100">2</div>
                      <p className="text-sm text-gray-600 leading-relaxed font-medium">Select your desired stream and medium carefully as these determine your academic path.</p>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold border border-blue-100">3</div>
                      <p className="text-sm text-gray-600 leading-relaxed font-medium">Print your submitted application and keep the reference number for future communication.</p>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold border border-blue-100">4</div>
                      <p className="text-sm text-gray-600 leading-relaxed font-medium text-red-600">Submit Original LC, 10th marksheet & Aadhar xerox with processing fee (₹200).</p>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-100">
                    <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                      <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Help Desk
                      </h4>
                      <p className="text-blue-800 font-bold text-lg mb-1">8888673608</p>
                      <p className="text-xs text-blue-600 font-medium italic">Available: 11:00 AM - 04:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Form Area */}
            <div className={`transition-all duration-1000 delay-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl shadow-blue-900/10 border border-white overflow-hidden">

                {/* Status Alert Overlay */}
                {submitStatus.type && (
                  <div className={`p-6 border-b transition-all duration-300 ${submitStatus.type === 'success'
                    ? 'bg-green-50 border-green-100 text-green-800'
                    : 'bg-red-50 border-red-100 text-red-800'
                    }`}>
                    <div className="flex items-center gap-3">
                      {submitStatus.type === 'success' ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                      <div>
                        <h4 className="font-bold">{submitStatus.type === 'success' ? 'Application Success' : 'Attention Required'}</h4>
                        <p className="text-sm">{submitStatus.message}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-10">

                  {/* Branch Selection */}
                  <div className="space-y-4">
                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 uppercase tracking-wider">
                      <GraduationCap className="w-4 h-4 text-blue-500" />
                      Academic Branch <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="branch_name"
                      value={formData.branch_name}
                      onChange={handleChange}
                      required
                      className="w-full h-14 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 transition-all outline-none text-gray-700 appearance-none font-medium"
                    >
                      <option value="">Choose your campus</option>
                      <option value="Lokmanya High School & Jr. College Nallasopara">Lokmanya High School & Jr. College Nallasopara</option>
                      <option value="Bharti Academy High School & Jr. College Umergoan">Bharti Academy High School & Jr. College Umergoan</option>
                      <option value="Bharti Academy English High School & Jr. College Virar">Bharti Academy English High School & Jr. College Virar</option>
                    </select>
                  </div>

                  {/* Personal Section */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 mb-2">
                      <span className="h-px bg-gray-200 flex-1"></span>
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                        <User className="w-3 h-3" /> Student Profile
                      </h3>
                      <span className="h-px bg-gray-200 flex-1"></span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">First Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          required
                          placeholder="John"
                          className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Last Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          required
                          placeholder="Doe"
                          className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Father's Full Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="father_name"
                          value={formData.father_name}
                          onChange={handleChange}
                          required
                          placeholder="Father's name"
                          className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Mother's Full Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="mother_name"
                          value={formData.mother_name}
                          onChange={handleChange}
                          required
                          placeholder="Mother's name"
                          className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Section */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 mb-2">
                      <span className="h-px bg-gray-200 flex-1"></span>
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                        <Phone className="w-3 h-3" /> Contact & Identification
                      </h3>
                      <span className="h-px bg-gray-200 flex-1"></span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Mobile Phone <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="tel"
                            name="phone_no"
                            value={formData.phone_no}
                            onChange={handleChange}
                            required
                            placeholder="Primary Contact"
                            className="w-full h-12 pl-11 pr-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">WhatsApp Number <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                          <input
                            type="tel"
                            name="whatsapp_no"
                            value={formData.whatsapp_no}
                            onChange={handleChange}
                            required
                            placeholder="WhatsApp for alerts"
                            className="w-full h-12 pl-11 pr-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Aadhar Number <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="aadhar_number"
                          value={formData.aadhar_number}
                          onChange={handleChange}
                          required
                          maxLength={12}
                          placeholder="12 Digit ID"
                          className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="email"
                            name="email_id"
                            value={formData.email_id}
                            onChange={handleChange}
                            placeholder="example@domain.com"
                            className="w-full h-12 pl-11 pr-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Demographics */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase ml-1 flex items-center gap-2">
                        <Calendar className="w-3 h-3" /> Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        required
                        className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-500 uppercase ml-1">Gender <span className="text-red-500">*</span></label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        required
                        className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                      >
                        <option value="">Select Gender</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Academic Choice */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 mb-2">
                      <span className="h-px bg-gray-200 flex-1"></span>
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                        <BookOpen className="w-3 h-3" /> Academic Details
                      </h3>
                      <span className="h-px bg-gray-200 flex-1"></span>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Admission Year</label>
                        <input
                          type="text"
                          name="admission_year"
                          value={formData.admission_year}
                          readOnly
                          className="w-full h-12 px-5 bg-blue-50 border-2 border-blue-100 rounded-xl font-bold text-blue-700 outline-none cursor-not-allowed"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Select Stream <span className="text-red-500">*</span></label>
                        <select
                          name="stream"
                          value={formData.stream}
                          onChange={handleChange}
                          required
                          className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                        >
                          <option value="">Stream</option>
                          <option value="Arts">Arts</option>
                          <option value="Commerce">Commerce</option>
                          <option value="Science">Science</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 uppercase ml-1">Language Medium <span className="text-red-500">*</span></label>
                        <select
                          name="medium"
                          value={formData.medium}
                          onChange={handleChange}
                          required
                          className="w-full h-12 px-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium"
                        >
                          <option value="">Medium</option>
                          <option value="English">English</option>
                          <option value="Marathi">Marathi</option>
                          <option value="Semi-English">Semi-English</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1 flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> Residential Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder="Complete Postal Address"
                      rows={4}
                      className="w-full p-5 bg-gray-50 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:bg-white transition-all outline-none font-medium resize-none shadow-inner"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4 flex flex-col items-center gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full md:w-max px-12 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-2xl hover:from-blue-700 hover:to-indigo-700 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-blue-500/30 disabled:opacity-70 disabled:hover:scale-100 flex items-center justify-center gap-3 text-lg"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin w-6 h-6" />
                          Processing Application...
                        </>
                      ) : (
                        <>
                          Confirm & Submit Enrollment
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 font-medium italic">By submitting, you agree to the institute's admission policies.</p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx global>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        .hero-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}
